// src/components/SyncConflictBanner.tsx

/**
 * SyncConflictBanner - the human surface for cross-device sync conflicts.
 *
 * Renders only when the project diverged on another device. It shows each
 * difference in plain language and lets the person choose a side. Until they
 * choose, nothing is overwritten. Mounted once, globally (see App.tsx), it is a
 * no-op for everyone whose project is in sync.
 */

import { useState } from "react";
import { CloudOff, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSyncConflicts, type ConflictSide } from "@/hooks/useSyncConflicts";
import { useI18n } from "@/i18n/I18nContext";

export function SyncConflictBanner() {
  const { conflicts, hasConflicts, resolve, resolveAll } = useSyncConflicts();
  const [busy, setBusy] = useState(false);
  const { t } = useI18n();

  if (!hasConflicts) return null;

  const many = conflicts.length > 1;

  const choose = (id: string, side: ConflictSide) => { setBusy(true); resolve(id, side); };
  const chooseAll = (side: ConflictSide) => { setBusy(true); resolveAll(side); };

  return (
    <div
      role="alertdialog"
      aria-label={t("sync.conflict.ariaLabel")}
      className="fixed bottom-4 right-4 z-50 w-[min(92vw,420px)] rounded-xl border border-border bg-background shadow-xl"
    >
      <div className="flex items-start gap-3 border-b border-border p-4">
        <CloudOff className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" aria-hidden />
        <div>
          <p className="text-sm font-semibold text-foreground">{t("sync.conflict.title")}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t("sync.conflict.desc")}
          </p>
        </div>
      </div>

      <ul className="max-h-64 overflow-y-auto p-2">
        {conflicts.map(c => (
          <li key={c.id} className="rounded-lg p-2 hover:bg-muted/50">
            <p className="px-1 text-xs text-foreground">{c.description}</p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="default" disabled={busy} onClick={() => choose(c.id, "mine")} className="flex-1">
                {t("sync.conflict.keepMine")}
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => choose(c.id, "theirs")} className="flex-1">
                {t("sync.conflict.useTheirs")}
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {many && (
        <div className="flex items-center justify-between gap-2 border-t border-border p-3">
          <span className="text-xs text-muted-foreground">{t("sync.conflict.diffCount", { n: conflicts.length })}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => chooseAll("mine")}>
              <Check className="mr-1 h-3.5 w-3.5" aria-hidden /> {t("sync.conflict.keepAllMine")}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => chooseAll("theirs")}>
              {t("sync.conflict.useAllTheirs")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
